import { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { WizardStepper } from '@/components/custom/WizardStepper';
import { SUPPORT_CATEGORIES } from '@uniticket/shared';
import { createTicketWithExtras } from '@/hooks/useTickets';
import { AttachmentPicker } from '@/components/tickets/AttachmentPicker';
import { SimilarTicketsBanner, SimilarTicketsConfirmDialog } from '@/components/tickets/SimilarTicketsBanner';
import type { SimilarTicketItem } from '@uniticket/shared';

export function SupportWizard() {
  const navigate = useNavigate();
  const { t } = useTranslation(['tickets', 'common']);
  const [step, setStep] = useState(0);

  const [category, setCategory] = useState('');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('NORMAL');
  const [steps, setSteps] = useState('');

  // Customer contact
  const [contactFirstName, setContactFirstName] = useState('');
  const [contactLastName, setContactLastName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactOpen, setContactOpen] = useState(false);

  // Customer email capture (files + pasted text) — both forwarded after ticket create
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [pastedEmail, setPastedEmail] = useState('');

  // PRD 9.2.5: auto-capture browser info (read-only)
  const browserInfo = useMemo(() => {
    const ua = navigator.userAgent;
    const browserMatch = ua.match(/(Chrome|Firefox|Safari|Edg|Opera)\/[\d.]+/);
    const browser = browserMatch ? browserMatch[0].replace('Edg', 'Edge') : 'Unknown';
    return `${browser} on ${navigator.platform}, ${window.innerWidth}x${window.innerHeight}`;
  }, []);

  const categoryLabels: Record<string, string> = {
    BUG_REPORT: t('common:supportCategory.bug_report'),
    FEATURE_REQUEST: t('common:supportCategory.feature_request'),
    QUESTION: t('common:supportCategory.question'),
    IT_PROVISIONING: t('common:supportCategory.it_provisioning'),
    DAMAGE: t('common:supportCategory.damage'),
    OTHER: t('common:supportCategory.other'),
  };

  const [submitting, setSubmitting] = useState(false);
  const [similarResults, setSimilarResults] = useState<SimilarTicketItem[]>([]);
  const [showSimilarConfirm, setShowSimilarConfirm] = useState(false);

  function handleSubmitClick() {
    if (!subject.trim()) { toast.error(t('common:validation.subjectRequired')); return; }
    if (!category) { toast.error(t('common:validation.categoryRequired')); return; }
    if (similarResults.length > 0 && !showSimilarConfirm) {
      setShowSimilarConfirm(true);
      return;
    }
    doSubmit();
  }

  async function doSubmit() {
    setShowSimilarConfirm(false);
    setSubmitting(true);
    try {
      const ua = navigator.userAgent;
      const result = await createTicketWithExtras({
        ticketType: 'SUPPORT_REQUEST',
        subject: subject.trim(),
        description: [description.trim(), steps ? `Steps to reproduce:\n${steps}` : ''].filter(Boolean).join('\n\n') || undefined,
        priority,
        supportCategory: category,
        browserInfo: {
          userAgent: ua,
          platform: navigator.platform,
          screenSize: `${window.innerWidth}x${window.innerHeight}`,
          display: browserInfo,
        },
        // Customer contact
        contactFirstName: contactFirstName.trim() || undefined,
        contactLastName: contactLastName.trim() || undefined,
        contactPhone: contactPhone.trim() || undefined,
        contactEmail: contactEmail.trim() || undefined,
      }, attachedFiles, pastedEmail);
      toast.success(t('tickets:create.created', { number: result.ticket.ticketNumber }));
      if (result.uploadErrors.length > 0) {
        toast.warning(t('tickets:customerEmail.someUploadsFailed', {
          count: result.uploadErrors.length,
          names: result.uploadErrors.map((e) => e.fileName).join(', '),
        }));
      }
      if (result.textAttachmentFailed) {
        toast.warning(t('tickets:customerEmail.textAttachmentFailed'));
      }
      navigate(`/tickets/${result.ticket.id}`);
    } catch (err: any) {
      toast.error(err?.message || t('tickets:create.createError'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="max-w-4xl mx-auto">
      <WizardStepper
        steps={[t('tickets:create.steps.details'), t('tickets:create.steps.review')]}
        currentStep={step}
      />

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 mt-8">
        {/* Step 0: Details */}
        {step === 0 && (
          <div>
            <h2 className="text-xl font-bold text-slate-900 mb-1">{t('tickets:supportWizard.detailsTitle')}</h2>
            <p className="text-sm text-slate-500 mb-6">{t('tickets:supportWizard.detailsHint')}</p>

            <div className="space-y-4 max-w-lg">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  {t('tickets:supportWizard.category')} <span className="text-red-500">*</span>
                </label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full px-4 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40"
                >
                  <option value="">{t('tickets:supportWizard.selectCategory')}</option>
                  {SUPPORT_CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>{categoryLabels[cat] || cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  {t('tickets:supportWizard.subject')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder={t('tickets:supportWizard.subjectPlaceholder')}
                  maxLength={200}
                  className="w-full px-4 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40"
                />
                <p className="text-xs text-slate-400 mt-1 text-right">{subject.length}/200</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  {t('tickets:supportWizard.description')} <span className="text-red-500">*</span>
                </label>
                <textarea
                  rows={4}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t('tickets:supportWizard.descriptionPlaceholder')}
                  className="w-full px-4 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40 resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('tickets:supportWizard.priority')}</label>
                <select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  className="w-full px-4 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40"
                >
                  <option value="LOW">{t('common:priority.LOW')}</option>
                  <option value="NORMAL">{t('common:priority.NORMAL')}</option>
                  <option value="HIGH">{t('common:priority.HIGH')}</option>
                  <option value="URGENT">{t('common:priority.URGENT')}</option>
                </select>
              </div>

              <SimilarTicketsBanner
                subject={subject}
                description={description}
                ticketType="SUPPORT_REQUEST"
                onResultsChange={setSimilarResults}
              />

              {/* Bug-specific fields */}
              {category === 'BUG_REPORT' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    {t('tickets:supportWizard.stepsToReproduce')}
                  </label>
                  <textarea
                    rows={3}
                    value={steps}
                    onChange={(e) => setSteps(e.target.value)}
                    placeholder={t('tickets:supportWizard.stepsPlaceholder')}
                    className="w-full px-4 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40 resize-none"
                  />
                </div>
              )}

              {/* Auto-captured browser info (PRD 9.2.5: read-only) */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  {t('tickets:supportWizard.browserEnv')} <span className="text-slate-400 text-xs">{t('tickets:supportWizard.autoDetected')}</span>
                </label>
                <input
                  type="text"
                  value={browserInfo}
                  readOnly
                  className="w-full px-4 py-2.5 text-sm border border-slate-200 rounded-lg bg-slate-50 text-slate-500 cursor-not-allowed"
                />
              </div>
            </div>

            {/* Customer email capture — paste + file attachments */}
            <div className="mt-6 max-w-lg space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  {t('tickets:customerEmail.label')}
                </label>
                <p className="text-xs text-slate-400 mb-2">{t('tickets:customerEmail.hint')}</p>
                <textarea
                  rows={6}
                  value={pastedEmail}
                  onChange={(e) => setPastedEmail(e.target.value)}
                  placeholder={t('tickets:customerEmail.placeholder')}
                  className="w-full px-4 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40 font-mono resize-y"
                />
              </div>
              <AttachmentPicker files={attachedFiles} onChange={setAttachedFiles} disabled={submitting} />
            </div>

            {/* Customer Contact — collapsible */}
            <div className="mt-6 border border-slate-200 rounded-xl overflow-hidden max-w-lg">
              <button
                onClick={() => setContactOpen(!contactOpen)}
                className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
              >
                <span className="text-sm font-semibold text-slate-700">{t('tickets:supportWizard.customerContact')}</span>
                <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${contactOpen ? 'rotate-180' : ''}`} />
              </button>
              {contactOpen && (
                <div className="px-4 py-4">
                  <p className="text-xs text-slate-500 mb-3">{t('tickets:supportWizard.customerContactHint')}</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">{t('common:fields.contactFirstName')}</label>
                      <input type="text" value={contactFirstName} onChange={(e) => setContactFirstName(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">{t('common:fields.contactLastName')}</label>
                      <input type="text" value={contactLastName} onChange={(e) => setContactLastName(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">{t('common:fields.contactPhone')}</label>
                      <input type="tel" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">{t('common:fields.contactEmail')}</label>
                      <input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40" />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 1: Review */}
        {step === 1 && (
          <div>
            <h2 className="text-xl font-bold text-slate-900 mb-1">{t('tickets:supportWizard.reviewTitle')}</h2>
            <p className="text-sm text-slate-500 mb-6">{t('tickets:supportWizard.reviewHint')}</p>

            <div className="space-y-4">
              <div className="border border-slate-200 rounded-lg p-4">
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">{t('common:ticketType.SUPPORT_REQUEST')}</h3>
                <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                  <ReviewField label={t('common:fields.type')} value={t('common:ticketType.SUPPORT_REQUEST')} />
                  <ReviewField label={t('tickets:supportWizard.category')} value={categoryLabels[category] || category || '—'} />
                  <ReviewField label={t('common:fields.subject')} value={subject || '—'} />
                  <ReviewField label={t('common:fields.priority')} value={t(`common:priority.${priority}`)} />
                  <div className="col-span-2">
                    <ReviewField label={t('common:fields.description')} value={description || '—'} />
                  </div>
                  <div className="col-span-2">
                    <ReviewField label={t('tickets:supportWizard.browserEnv')} value={browserInfo} />
                  </div>
                  {steps && (
                    <div className="col-span-2">
                      <ReviewField label={t('tickets:supportWizard.stepsToReproduce')} value={steps} />
                    </div>
                  )}
                </div>
              </div>

              {(contactFirstName || contactLastName || contactPhone || contactEmail) && (
                <div className="border border-slate-200 rounded-lg p-4">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">{t('tickets:supportWizard.customerContact')}</h3>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                    <ReviewField label={t('common:fields.contactFirstName')} value={contactFirstName || '—'} />
                    <ReviewField label={t('common:fields.contactLastName')} value={contactLastName || '—'} />
                    <ReviewField label={t('common:fields.contactPhone')} value={contactPhone || '—'} />
                    <ReviewField label={t('common:fields.contactEmail')} value={contactEmail || '—'} />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between mt-6">
        <div>
          {step === 0 ? (
            <Link to="/tickets/new" className="px-5 py-2.5 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
              {t('common:actions.cancel')}
            </Link>
          ) : (
            <button onClick={() => setStep(0)} className="px-5 py-2.5 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
              {t('common:actions.previous')}
            </button>
          )}
        </div>
        <div>
          {step === 0 ? (
            <button onClick={() => setStep(1)} className="px-5 py-2.5 text-sm font-semibold text-white rounded-lg bg-gradient-to-r from-brand-rose to-brand-orange hover:from-brand-purple hover:to-brand-rose transition-all">
              {t('common:actions.next')}
            </button>
          ) : (
            <button onClick={handleSubmitClick} disabled={submitting} className="px-5 py-2.5 text-sm font-semibold text-white rounded-lg bg-gradient-to-r from-brand-rose to-brand-orange hover:from-brand-purple hover:to-brand-rose transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {submitting ? t('tickets:create.creating') : t('tickets:create.submitTicket')}
            </button>
          )}
        </div>
      </div>
      {showSimilarConfirm && similarResults.length > 0 && (
        <SimilarTicketsConfirmDialog
          results={similarResults}
          onConfirm={doSubmit}
          onCancel={() => setShowSimilarConfirm(false)}
        />
      )}
    </div>
  );
}

function ReviewField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">{label}</p>
      <p className="text-sm text-slate-700 whitespace-pre-line">{value}</p>
    </div>
  );
}
